import { ContentState, convertToRaw } from "draft-js";
import { I18n } from "@assets/strings";
import React, { JSX } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import instantLiveBlue from "@assets/images/live/instant_live_blue.svg";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { LivecourseStatus } from "@domain/livecourse/livecourse";
import { Category } from "@domain/lms/category/category";
import { TooshareNotification } from "@domain/notification/notification";
import { formatTimeAgo } from "@utils/date";
import { useIsMobile } from "@utils/hooks";
import { gap } from "@ui/styles/utils";
import { OnGoingLiveCourseIcon } from "@ui/components/animatedIcons/onGoingLiveCourseIcon";
import { Primary } from "@ui/components/containers/primary";
import {
	ActiveInstructiveIcon,
	ArticleIcon,
	CommentIcon,
	CommunityIcon,
	ErrorIcon,
	FileIcon,
	HatIcon,
	LikeIcon,
	MemberIcon,
	QuizIcon,
	RepostIcon,
	ShareroomIcon,
	StarIcon,
} from "@ui/components/icons";
import { LinkExternal } from "@ui/components/linkExternal";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { GroupAccessButton } from "./groupAccessButton";
import { JoinGroupButton } from "./joinGroupButton";
import { NotificationFollowButton } from "./notificationFollowButton";
import { NotificationJoinButton } from "./notificationJoinButton";
import { NotificationSource } from "./notificationSource";

interface NotificationViewProps extends React.HTMLAttributes<HTMLDivElement> {
	notification: TooshareNotification;
	close: () => void;
}
export const NotificationView: React.FC<NotificationViewProps> = ({ notification, close, ...htmlProps }) => {
	const notificationInfos = useNotificationInfos(notification);
	const currentProfile = useCurrentProfile();
	const { notificationService } = useServices();

	if (!currentProfile || !notificationInfos) {
		return null;
	}
	const { icon, path, wording, teasing, interactiveButton } = notificationInfos;
	return (
		<Container {...htmlProps}>
			{notification.source && (
				<SourcePicture
					to={
						notification.type === "USER_LINKED_TO_INSTITUTE"
							? Path.Organization(notification.institute)
							: notification.source.type === "USER"
								? Path.Profile(notification.source)
								: Path.Organization(notification.source)
					}
				>
					<ProfilePicture
						// TODO: Change avatarUrl for notifications : notification.type === "USER_LINKED_TO_INSTITUTE" ? notification.institute.avatarUrl : notification.source.avatar
						picture={notification.source.avatar}
						fallbackType={notification.source.type}
						size={"L"}
					/>
					<IconWrapper>{icon}</IconWrapper>
				</SourcePicture>
			)}
			<div>
				{!!notification.source && notification.type !== "USER_LINKED_TO_INSTITUTE" && (
					<NotificationSource onClick={close} source={notification.source} />
				)}
				<LinkExternal
					to={
						!path || notification.type === "NEW_LIVE_INSTANT_COURSE" || notification.type === "NEW_LIVE_SCHEDULE_COURSE"
							? ""
							: path
					}
					onClick={
						notification.type !== "NEW_LIVE_INSTANT_COURSE" && notification.type !== "NEW_LIVE_SCHEDULE_COURSE"
							? () => {
									if (!notification.read) {
										notificationService.read(notification.id);
									}
									close();
								}
							: undefined
					}
				>
					<Primary>{wording}</Primary>
					<Teasing>{teasing}</Teasing>
					<NotificationDate>{formatTimeAgo(notification.date)}</NotificationDate>
				</LinkExternal>
				{interactiveButton}
			</div>
			<Grow />
			<ReadIcon read={notification.read} onClick={() => notificationService.read(notification.id)} />
		</Container>
	);
};

function useNotificationInfos(notification: TooshareNotification): {
	path: string | undefined;
	wording: string;
	teasing?: string;
	icon: JSX.Element;
	interactiveButton?: JSX.Element;
} | null {
	const isMobile = useIsMobile();
	const currentProfile = useCurrentProfile();
	const gender = currentProfile?.gender?.toLowerCase() ?? "other";

	switch (notification.type) {
		case "NEW_FOLLOWER":
			return {
				path: Path.Profile(notification.source),
				wording: I18n.t("notification.follower"),
				icon: <MemberIcon />,
				interactiveButton: <NotificationFollowButton userId={notification.source.id} />,
			};
		case "NEW_POST_MENTION":
			return {
				path: Path.Post(notification.post.id),
				wording: I18n.t(`notification.post.new.${notification.organization ? "organization" : gender}`, {
					name: notification.organization?.primaryName,
				}),
				teasing: notification.post.text?.getPlainText(),
				icon: <div>@</div>,
			};
		case "NEW_POST_LIKE":
			return {
				path: Path.Post(notification.post.id),
				wording: I18n.t(
					notification.organization
						? "notification.post.mention.like.organization"
						: notification.author.id === currentProfile?.id
							? "notification.post.self.like"
							: `notification.post.mention.like.${gender}`,
					{ name: notification.organization?.primaryName }
				),
				teasing: notification.post.text?.getPlainText(),
				icon: <LikeIcon />,
			};
		case "NEW_POST_SHARE":
			return {
				path: Path.Post(notification.post.id),
				wording: I18n.t(
					notification.organization
						? "notification.post.mention.share.organization"
						: notification.author.id === currentProfile?.id
							? "notification.post.self.share"
							: `notification.post.mention.share.${gender}`,
					{ name: notification.organization?.primaryName }
				),
				teasing: notification.post.text?.getPlainText(),
				icon: <RepostIcon />,
			};
		case "NEW_APPLICATION_POST_VIDEO":
			return {
				path: Path.Post(notification.post.id),
				wording: I18n.t("notification.share_application.new"),
				teasing: notification.post.text?.getPlainText(),
				icon: <RepostIcon />,
			};
		case "NEW_POST_COMMENT":
			return {
				path: Path.Post(notification.post.id),
				wording: I18n.t(
					notification.organization
						? isMentioned(notification.comment.text, notification.organization.id)
							? "notification.comment.organization"
							: "notification.post.mention.comment.organization"
						: notification.author.id === currentProfile?.id
							? "notification.post.self.comment"
							: !!currentProfile && isMentioned(notification.comment.text, currentProfile?.id)
								? `notification.comment.${gender}`
								: `notification.post.mention.comment.${gender}`,
					{ name: notification.organization?.primaryName }
				),
				teasing: notification.comment.text.getPlainText(),
				icon: <CommentIcon />,
			};
		case "NEW_COURSE":
			return {
				path: Path.Lms.Course(notification.course.id),
				wording: I18n.t("notification.course.new"),
				teasing: notification.course.title,
				icon: <FileIcon />,
			};
		case "NEW_COURSE_FEEDBACK":
			return {
				path: Path.Lms.Course(notification.course.id),
				wording: I18n.t("notification.course.feedback.new"),
				teasing: notification.course.title,
				icon: <StarIcon />,
			};
		case "NEW_CAROUSEL":
			return {
				path: Path.ReferenceCarousel({
					id: notification.carousel?.id,
					category: notification.carousel?.category as Category,
					slug: notification.carousel?.slug,
				}),
				wording: I18n.t("notification.carousel.new"),
				teasing: notification.carousel?.title,
				icon: <FileIcon />,
			};

		case "NEW_CAROUSEL_INSTRUCTIVE":
			return {
				path: Path.ReferenceCarousel({
					id: notification.carousel?.carouselId,
					category: notification.carousel?.category as Category,
					slug: notification.carousel?.slug,
				}),
				wording: I18n.t("notification.carousel.instructive"),
				teasing: notification.carousel?.title,
				icon: <ActiveInstructiveIcon />,
			};
		case "NEW_ARTICLE":
			return {
				path: Path.Article(notification.article.id),
				wording: I18n.t("notification.article.new"),
				teasing: notification.article.title,
				icon: <ArticleIcon />,
			};
		case "NEW_ARTICLE_FEEDBACK":
			return {
				path: Path.Article(notification.article.id),
				wording: I18n.t("notification.article.feedback.new"),
				teasing: notification.article.title,
				icon: <CommentIcon />,
			};
		case "USER_LINKED_TO_INSTITUTE":
			return {
				path: Path.Organization(notification.institute),
				wording: I18n.t(`notification.institute.linked.${notification.role}.${gender}`, {
					institute: notification.institute.primaryName,
				}),
				icon: <HatIcon />,
			};
		case "COURSE_DELETED":
			return {
				path: Path.Profile(notification.byUser),
				wording: I18n.t(`notification.course.deleted`, {
					course: notification.course.title,
				}),
				icon: <FileIcon />,
			};
		case "ARTICLE_DELETED":
			return {
				path: Path.Profile(notification.byUser),
				wording: I18n.t("notification.article.deleted"),
				teasing: notification.article.title,
				icon: <FileIcon />,
			};
		case "NEW_QUIZ":
			return {
				path: Path.Quiz(notification.quiz.id, isMobile),
				wording: I18n.t("notification.quiz.new"),
				teasing: notification.quiz.title,
				icon: <QuizIcon />,
			};
		case "QUIZ_PARTICIPATION":
			return {
				path: Path.Quiz(notification.quiz.id, isMobile, notification.source.id),
				wording: I18n.t("notification.quiz.participation"),
				teasing: notification.quiz.title,
				icon: <QuizIcon />,
			};
		case "USER_INVITED_TO_GROUP":
			return {
				path: Path.Discussion(notification.group),
				wording: I18n.t(`notification.group.invited.${gender}`),
				teasing: notification.group.name,
				icon: <CommunityIcon />,
				interactiveButton: <JoinGroupButton groupId={notification.group.id} role={notification.role} />,
			};
		case "USER_ACCEPTED_TO_GROUP":
			return {
				path: Path.Discussion(notification.group),
				wording: I18n.t("notification.group.accepted"),
				teasing: notification.group.name,
				icon: <CommunityIcon />,
			};
		case "USER_REMOVED_FROM_GROUP":
			return {
				path: Path.Discussion(notification.group),
				wording: I18n.t(`notification.discussion.removed.${gender}`),
				teasing: notification.group.name,
				icon: <CommunityIcon />,
			};
		case "ACCESS_REQUESTED_FOR_GROUP":
			return {
				path: Path.Discussion(notification.group),
				wording: I18n.t("notification.discussion.request"),
				teasing: notification.group.name,
				icon: <CommunityIcon />,
				interactiveButton: (
					<GroupAccessButton
						groupType="DISCUSSION_GROUP"
						userId={notification.source.id}
						groupId={notification.group.id}
						groupName={notification.group.name}
					/>
				),
			};
		case "ACCESS_GRANTED_FOR_GROUP":
			return {
				path: Path.Discussion(notification.group),
				wording: I18n.t(`notification.discussion.granted`),
				teasing: notification.group.name,
				icon: <CommunityIcon />,
			};
		case "USER_INVITED_TO_SHAREROOM":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t(`notification.group.invited.${gender}`),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
				interactiveButton: <JoinGroupButton groupId={notification.group.id} role={notification.role} />,
			};
		case "USER_ACCEPTED_TO_SHAREROOM":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t("notification.group.accepted"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};
		case "USER_REMOVED_FROM_SHAREROOM":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t(`notification.shareroom.removed.${gender}`),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};
		case "ACCESS_REQUESTED_FOR_SHAREROOM":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t("notification.shareroom.request"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
				interactiveButton: (
					<GroupAccessButton
						groupType="SHAREROOM"
						userId={notification.source.id}
						groupId={notification.group.id}
						groupName={notification.group.name}
					/>
				),
			};
		case "ACCESS_GRANTED_FOR_SHAREROOM":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t(`notification.shareroom.granted`),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "NEW_USER_JOIN_TO_SHAREROOM":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t("notification.shareroom.subscription"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "USER_LEFT_SHAREROOM":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t(
					notification.group.license === "PREMIUM"
						? "shareroom.premium.unsubscribtion"
						: "shareroom.free.unsubscribtion"
				),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "NEW_SHAREROOM_POST":
			return {
				path: Path.Post(notification.post.id),
				wording: I18n.t("shareroom.notification.post"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "NEW_SHAREROOM_COURSE":
			return {
				path: Path.Lms.Course(notification.course.id),
				wording: I18n.t("shareroom.notification.course"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "NEW_SHAREROOM_QUIZ":
			return {
				path: Path.Quiz(notification.quiz.id, isMobile),
				wording: I18n.t("shareroom.notification.quiz"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "NEW_SHAREROOM_ARTICLE":
			return {
				path: Path.Article(notification.article.id),
				wording: I18n.t("shareroom.notification.insight"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "NEW_PREMIUM_SHAREROOM":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t("shareroom.status_change"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "NEW_GROUP_FEEDBACK":
			return {
				path: Path.Shareroom(notification.group),
				wording: I18n.t("shareroom.notification.review"),
				teasing: notification.group.name,
				icon: <ShareroomIcon />,
			};

		case "CONTENT_MODERATED":
			return {
				path: Path.Cgu,
				wording: I18n.t("notification.moderated", {
					content: `${I18n.t(`content.${notification.content.type}`)} "${notification.content.name}"`,
				}),
				icon: <ErrorIcon />,
				teasing: I18n.t(`report.motive.${notification.motive}`),
			};
		case "ORGANIZATION_DELETED":
			return {
				path: Path.Profile(notification.source),
				wording: I18n.t(`notification.organization.deleted`),
				icon: <HatIcon />,
				teasing: notification.organization.primaryName,
			};

		case "LIVE_COURSE_STARTED":
			return {
				path: notification?.liveCourse?.link,
				wording: I18n.t(notification.liveCourse.status === LivecourseStatus.STATUS ? "live.closed" : "live.starting"),
				teasing: notification?.liveCourse?.title,
				icon: <OnGoingLiveCourseIcon status={notification.liveCourse.status} />,
				interactiveButton:
					notification.liveCourse.status === LivecourseStatus.STATUS ? (
						<></>
					) : (
						<NotificationJoinButton liveCourseId={notification?.liveCourse?.id} />
					),
			};

		case "NEW_LIVE_INSTANT_COURSE":
			return {
				path: notification?.liveCourse?.link,
				wording: I18n.t(
					notification.liveCourse.status === LivecourseStatus.STATUS ? "live.closed" : "live.justlaunched"
				),
				teasing: notification?.liveCourse?.title,
				icon: <OnGoingLiveCourseIcon status={notification.liveCourse.status} />,
				interactiveButton:
					notification.liveCourse.status === LivecourseStatus.STATUS ? (
						<></>
					) : (
						<NotificationJoinButton liveCourseId={notification?.liveCourse?.id} />
					),
			};
		case "NEW_LIVE_SCHEDULE_COURSE":
			return {
				path: notification?.liveCourse?.link,
				wording: I18n.t(
					notification.liveCourse.status === LivecourseStatus.STATUS ? "live.closed" : "live.tobelaunched"
				),
				teasing: notification?.liveCourse?.title,
				icon: <img src={instantLiveBlue} width={20} height={20} />,
				interactiveButton:
					notification.liveCourse.status === LivecourseStatus.STATUS ? (
						<></>
					) : (
						<NotificationJoinButton liveCourseId={notification?.liveCourse?.id} />
					),
			};
		default:
			return null;
	}
}

function isMentioned(text: ContentState, id: string): boolean {
	const rawEntities = convertToRaw(text).entityMap;
	for (const key of Object.keys(rawEntities)) {
		if (rawEntities[key]?.type === "mention") {
			const data = rawEntities[key]?.data as { mention: { id: string } };
			if (data?.mention?.id === id) {
				return true;
			}
		}
	}
	return false;
}

const Container = styled.div`
	padding: 8px 20px;
	display: flex;
	align-items: flex-start;
	${gap(10, "row")};

	svg {
		cursor: pointer;
	}

	transition: opacity 0.1s linear;

	&:hover  {
		background-color: ${({ theme }) => theme.colors.lightergray};
	}

	button {
		margin-top: 10px;
	}
`;

const SourcePicture = styled(Link)`
	position: relative;
	flex-shrink: 0;
	margin-top: 4px;
`;

const IconWrapper = styled.div`
	width: 25px;
	height: 25px;
	border-radius: 13px;
	box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.3);
	background-color: ${({ theme }) => theme.colors.white};
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	right: 0;
	bottom: 0;
	color: ${({ theme }) => theme.colors.secondary};
	font-weight: 700;
	font-size: 0.9375rem;
	> svg {
		width: 18px;
		path,
		circle {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
`;

const Teasing = styled(Primary)`
	flex: 1;
	font-size: 0.8125rem;
	font-style: italic;
	display: -webkit-box;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
`;

const NotificationDate = styled.div`
	${({ theme }) => theme.textStyles.light};
	color: ${({ theme }) => theme.colors.textSecondary};
`;

const Grow = styled.div`
	flex: 1;
`;

const ReadIcon = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "read",
})<{ read: boolean }>`
	cursor: pointer;
	width: 10px;
	height: 10px;
	border-radius: 5px;
	transition: opacity 0.1s linear;
	opacity: 1;
	background-color: ${({ theme }) => theme.colors.red};
	${({ read }) => read && "opacity: 0"};
	flex-shrink: 0;
	margin: 4px 5px 0 0;
`;
